import React from 'react';
import { Paperclip, Upload, Download, Trash2, FileText, Image, File } from 'lucide-react';

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function getFileIcon(contentType) {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType?.includes('pdf')) return FileText;
  return File;
}

export function LeadDocumentsSection({ documents, isViewer, uploading, dragOver, onDragOver, onDragLeave, onDrop, onFileSelect, onDownload, onDelete }) {
  return (
    <div className="border border-blue-200 rounded-sm bg-blue-50/30" data-testid="lead-documents-section">
      <div className="p-4 border-b border-blue-200 flex items-center justify-between bg-blue-50">
        <div className="flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-blue-600" />
          <h2 className="font-heading font-semibold text-blue-900 text-sm">Documents ({documents.length})</h2>
        </div>
        {!isViewer && (
          <label className="cursor-pointer">
            <input type="file" className="hidden" onChange={onFileSelect} data-testid="document-file-input" />
            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-[#0047AB] bg-[#EFF6FF] border border-[#0047AB]/20 rounded-sm hover:bg-blue-100 transition-colors" data-testid="upload-document-btn">
              <Upload className="w-3 h-3" /> Upload
            </span>
          </label>
        )}
      </div>
      {!isViewer && (
        <div
          className={`mx-4 mt-4 border-2 border-dashed rounded-sm p-6 text-center transition-colors ${dragOver ? 'border-[#0047AB] bg-blue-50/50' : 'border-slate-200 hover:border-slate-300'}`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          data-testid="document-drop-zone"
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin w-4 h-4 border-2 border-[#0047AB] border-t-transparent rounded-full" />
              <span className="text-xs text-slate-500">Uploading...</span>
            </div>
          ) : (
            <>
              <Upload className="w-5 h-5 text-slate-300 mx-auto mb-1.5" />
              <p className="text-xs text-slate-400">Drag & drop a file here, or click Upload above</p>
              <p className="text-[10px] text-slate-300 mt-1">Max 25MB per file</p>
            </>
          )}
        </div>
      )}
      <div className="p-4 space-y-2">
        {documents.length > 0 ? documents.map(doc => {
          const IconComp = getFileIcon(doc.content_type);
          return (
            <div key={doc.id} className="flex items-center justify-between p-2.5 border border-slate-200 rounded-sm hover:bg-slate-50 group transition-colors" data-testid={`document-${doc.id}`}>
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <div className="w-8 h-8 bg-[#EFF6FF] rounded-sm flex items-center justify-center shrink-0">
                  <IconComp className="w-4 h-4 text-[#0047AB]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 truncate">{doc.filename}</p>
                  <p className="text-[10px] text-slate-400">{formatFileSize(doc.size)} &middot; {doc.uploaded_by_name} &middot; {new Date(doc.uploaded_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <button onClick={() => onDownload(doc)} className="p-1.5 hover:bg-blue-50 rounded-sm transition-colors" data-testid={`download-doc-${doc.id}`}>
                  <Download className="w-3.5 h-3.5 text-[#0047AB]" />
                </button>
                {!isViewer && (
                  <button onClick={() => onDelete(doc.id)} className="p-1.5 hover:bg-red-50 rounded-sm transition-colors" data-testid={`delete-doc-${doc.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                )}
              </div>
            </div>
          );
        }) : (
          <p className="text-sm text-slate-400 text-center py-4">No documents yet</p>
        )}
      </div>
    </div>
  );
}
